import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, from, map, mergeMap, of, switchMap, tap, timer, toArray } from 'rxjs';
import { UtilityDockStore } from '../utility-dock.store';
import { TaskService } from '@core/services/api/task.service';
import { IEBTask } from '@shared/models/entities/task.interface';
import { MemberTaskService } from '@core/services/api/member-task.service';
import { selectMember } from '@store/member/member.selectors';
import { IEBMemberTask } from '@shared/models/entities/relation.member-task.interface';
import { getKanbanStatusMeta } from '@shared/models/kanban-status.enum';
import { IEBMember } from '@shared/models/entities/member.interface';
import { ERROR_MESSAGES } from '@app/constants/error-messages.constants';
import { UI_TEXT } from '@app/constants/ui-text.constants';

@Injectable({ providedIn: 'root' })
export class AssignedTasksStore {
  private readonly dock = inject(UtilityDockStore);
  private readonly ngrxStore = inject(Store, { optional: true }) as Store | null;
  private readonly memberTaskApi = inject(MemberTaskService);
  private readonly taskApi = inject(TaskService);

  private readonly member: Signal<IEBMember | null> = this.ngrxStore
    ? toSignal(this.ngrxStore.select(selectMember), { initialValue: null })
    : signal(null);

  readonly assignments = signal<IEBMemberTask[]>([]);
  readonly tasksById = signal<Record<string, IEBTask>>({});
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly enabled = signal(false);

  readonly itemsVm = computed(() => {
    const tasksById = this.tasksById();
    return (this.assignments() ?? []).map((assignment) => {
      const taskId = String(assignment?.taskId ?? '').trim();
      const rawTask = (assignment?.task ?? null) as IEBTask | null;
      const cachedTask = taskId ? tasksById[taskId] ?? null : null;
      const task = this.pickBetterTask(rawTask, cachedTask);

      const title = String(task?.taskName ?? '').trim() || UI_TEXT.ASSIGNED_TASKS_STORE.TASK_FALLBACK_TITLE;
      const type = (task?.type ?? null) as string | null;

      const dueAt = this.toDate(task?.dueDate);
      const dueKind = this.dueKind(dueAt);

      const assignedAt = this.toDate(assignment?.assignedDate);
      const assignedAgeLabel = assignedAt ? this.formatAssignedAge(assignedAt) : null;
      const role = typeof assignment?.role === 'string' ? assignment.role : null;
      const assignedBy = this.buildAssignerLabel(assignment);

      const statusMeta = getKanbanStatusMeta(task?.status);
      const statusLabel = statusMeta.label || null;
      const statusColor = statusMeta.color || null;

      const priority = typeof task?.priority === 'string' ? task.priority : null;
      const contextLine = this.getContextLine(task);

      const checklistTotal = task ? this.checklist(task).length : 0;

      return {
        taskId,
        task,
        title,
        type,
        dueAt,
        dueKind,
        assignedAt,
        assignedAgeLabel,
        role,
        assignedBy,
        statusLabel,
        statusColor,
        priority,
        contextLine,
        checklistTotal: checklistTotal > 0 ? checklistTotal : null,
      } as const;
    });
  });

  constructor() {
    effect(() => {
      // Assigned tasks disabled - merged into inbox
      this.enabled.set(false);
    });

    // Load assigned tasks when member is available, regardless of dock state
    // Poll more frequently when dock is open, less frequently when closed
    effect((onCleanup) => {
      const memberId = (this.member()?.memberId ?? '').trim();
      if (!memberId) {
        this.assignments.set([]);
        this.tasksById.set({});
        this.loading.set(false);
        this.error.set(null);
        return;
      }

      // Poll every 30s when dock is open and on TODOS tab, otherwise every 90s
      // Assigned tasks disabled - merged into inbox
      const isDockOpenOnTodos = false;
      const intervalMs = isDockOpenOnTodos ? 30_000 : 90_000;

      const sub = timer(0, intervalMs)
        .pipe(switchMap(() => this.loadOnce(memberId)))
        .subscribe();

      onCleanup(() => sub.unsubscribe());
    });
  }

  setEnabled(enabled: boolean): void {
    this.enabled.set(enabled);
  }

  refresh(): Promise<void> {
    const memberId = (this.member()?.memberId ?? '').trim();
    if (!memberId) return Promise.resolve();
    return firstValueFrom(this.loadOnce(memberId)).then(() => void 0);
  }

  private loadOnce(memberId: string) {
    this.loading.set(true);
    this.error.set(null);
    return this.memberTaskApi.findByMember(memberId).pipe(
      catchError(() => {
        this.error.set(ERROR_MESSAGES.UTILITY_DOCK.UNABLE_TO_LOAD_ASSIGNED_TASKS);
        return of([] as IEBMemberTask[]);
      }),
      switchMap((assignments) => this.enrichAssignments(assignments ?? [])),
      tap((assignments) => this.assignments.set(assignments)),
      tap(() => this.loading.set(false)),
      tap(() => void 0),
    );
  }

  private enrichAssignments(assignments: IEBMemberTask[]) {
    const list = assignments ?? [];
    if (!list.length) return of([] as IEBMemberTask[]);

    const tasksById = this.tasksById();
    const idsToHydrate = Array.from(
      new Set(
        list
          .filter((assignment) => {
            const id = String(assignment?.taskId ?? '').trim();
            if (!id) return false;
            return this.needsHydration(assignment?.task ?? tasksById[id] ?? null);
          })
          .map((assignment) => String(assignment?.taskId ?? '').trim())
          .filter(Boolean),
      ),
    ).slice(0, 75);

    if (!idsToHydrate.length) return of(list);

    return from(idsToHydrate).pipe(
      mergeMap((taskId) => {
        const cached = tasksById[taskId] ?? null;
        if (cached && !this.needsHydration(cached)) return of(cached);
        return this.taskApi.findOne(taskId).pipe(catchError(() => of(null)));
      }, 6),
      toArray(),
      tap((tasks) => {
        const next: Record<string, IEBTask> = { ...this.tasksById() };
        for (const task of tasks) {
          if (task?.taskId) next[task.taskId] = task;
        }
        this.tasksById.set(next);
      }),
      map(() => {
        const nextCache = this.tasksById();
        return list.map((a) => {
          const taskId = String(a?.taskId ?? '').trim();
          if (!taskId) return a;
          const cached = nextCache[taskId] ?? null;
          const mergedTask = this.pickBetterTask(a.task ?? null, cached);
          return { ...a, task: mergedTask ?? a.task ?? null };
        });
      }),
    );
  }

  private pickBetterTask(a: IEBTask | null, b: IEBTask | null): IEBTask | null {
    if (b && !this.needsHydration(b)) return b;
    if (a) return a;
    return b;
  }

  private needsHydration(task: IEBTask | null | undefined): boolean {
    if (!task) return true;
    const action = this.getActionLike(task);
    if (!action) return true;
    const actionName = typeof action.actionName === 'string' ? action.actionName.trim() : '';
    if (!actionName) return true;
    const campaignId = typeof action.campaignId === 'string' ? action.campaignId.trim() : '';
    const campaignName =
      typeof action.campaign?.campaignName === 'string' ? action.campaign.campaignName.trim() : '';
    if (campaignId && !campaignName) return true;
    return false;
  }

  private getContextLine(task: IEBTask | null): string | null {
    const action = task ? this.getActionLike(task) : null;
    const actionName = typeof action?.actionName === 'string' ? action.actionName.trim() : '';
    const campaignName =
      typeof action?.campaign?.campaignName === 'string' ? action.campaign.campaignName.trim() : '';
    if (campaignName && actionName) return `${campaignName} › ${actionName}`;
    if (campaignName) return campaignName;
    if (actionName) return actionName;
    return null;
  }

  private dueKind(dueAt: Date | null): 'overdue' | 'today' | 'future' | 'none' {
    if (!dueAt) return 'none';
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    if (dueAt < start) return 'overdue';
    if (dueAt >= start && dueAt <= end) return 'today';
    return 'future';
  }

  private buildAssignerLabel(assignment: IEBMemberTask | null | undefined): string | null {
    const first = (assignment?.assignerFirstName ?? '').trim();
    const last = (assignment?.assignerLastName ?? '').trim();
    const full = `${first} ${last}`.trim();
    const displayName = (assignment?.assignerDisplayName ?? '').trim();
    const email = (assignment?.assignerEmail ?? '').trim();
    const memberId = (assignment?.assignerMemberId ?? '').trim();
    return full || displayName || email || memberId || null;
  }

  private formatAssignedAge(assignedAt: Date): string {
    const now = new Date();
    const ms = now.getTime() - assignedAt.getTime();
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    if (Number.isNaN(days) || days < 0) return UI_TEXT.ASSIGNED_TASKS_STORE.ASSIGNED_JUST_NOW;
    if (days === 0) return UI_TEXT.ASSIGNED_TASKS_STORE.ASSIGNED_TODAY;
    if (days === 1) return UI_TEXT.ASSIGNED_TASKS_STORE.ASSIGNED_ONE_DAY_AGO;
    return UI_TEXT.ASSIGNED_TASKS_STORE.ASSIGNED_DAYS_AGO(days);
  }

  private toDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  private checklist(task: IEBTask): { id: string; text: string; required: boolean }[] {
    const parsed = this.safeParseJson(task.data);
    if (!this.isChecklistSchemaV1(parsed)) return [];
    const list = parsed.checklist;
    return list
      .map((i) => ({
        id: String(i.id ?? ''),
        text: String(i.text ?? i.label ?? ''),
        required: !!i.required,
      }))
      .filter((i) => i.id && i.text);
  }

  private safeParseJson(raw: string | null): unknown {
    if (!raw) return null;
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private getActionLike(task: IEBTask): {
    actionName?: unknown;
    campaignId?: unknown;
    campaign?: { campaignName?: unknown } | null;
  } | null {
    const maybeTask = task as unknown;
    if (!this.isRecord(maybeTask)) return null;
    const action = (maybeTask as Record<string, unknown>)['action'];
    if (!this.isRecord(action)) return null;
    const actionRecord = action as Record<string, unknown>;
    const campaign = actionRecord['campaign'];
    const campaignObj =
      this.isRecord(campaign) ? (campaign as { campaignName?: unknown }) : null;
    return {
      actionName: actionRecord['actionName'],
      campaignId: actionRecord['campaignId'],
      campaign: campaignObj,
    };
  }

  private isChecklistSchemaV1(
    value: unknown,
  ): value is {
    schemaVersion: 1;
    checklist: {
      id?: unknown;
      text?: unknown;
      label?: unknown;
      required?: unknown;
    }[];
  } {
    if (!this.isRecord(value)) return false;
    if (value['schemaVersion'] !== 1) return false;
    const checklist = value['checklist'];
    return Array.isArray(checklist);
  }
}
